import Axios from 'axios';
import * as Routes from 'modules/routes';
import React, { useState, useReducer, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { MultiBackend } from 'react-dnd-multi-backend';
import { HTML5toTouch } from 'rdndmb-html5-to-touch';
import I18n from 'modules/i18n';
import BuilderContext from 'components/common/Context';
import { useModalManager } from 'components/common/Modal';
import { useDebouncedEffect } from 'components/common/Utils';
import LinkVocabularyWrapper from './LinkVocabularyButton/LinkVocabularyWrapper';
import Container from './Container';
import DraggableChoiceEditor from './DraggableChoiceEditor';
import DropZoneButton from './DropZone/DropZoneButton';
import BackgroundImageButton from './BackgroundImageButton';
import SettingsButton from './SettingsButton';
import SectionHeader from './SectionHeader';
import backgroundReducer from './reducers/backgroundReducer';
import dropZoneReducer from './reducers/dropZoneReducer';
import draggableChoiceReducer from './reducers/draggableChoiceReducer';
import correctAnswerReducer from './reducers/correctAnswerReducer';
import dragAndDropSettingsReducer from './reducers/dragAndDropSettingsReducer';
import textAreaReducer from './reducers/textAreaReducer';
import AddTextAreaButton from './TextArea/AddTextAreaButton';
import useVocabularyStore from '../useVocabularyStore';
import CustomDragLayer from './CustomDragLayer';
import styles from './DragAndDrop.module.scss';
import CopyingText from './CopyingText';
import { formatVocab, measureHeightsPct, computeSpacedTextAreas, matchDropzonesToTextAreas } from '../utils';
import { DragAndDropSettings } from '../types';

const SNAP_TO_GRID = true;

export interface SaveControl {
  flushPendingSave: () => Promise<void>;
  hasPendingSave: () => boolean;
}

interface BuilderProps {
  backgroundImage?: {
    id: number;
    url: string;
    position?: string;
    size?: string;
  };
  data: {
    correct_answers: object;
    draggable_choices: object;
    drop_zones: object;
    text_areas: object;
    has_reusable_answer_choices?: boolean;
    has_transparent_answer_choices: boolean;
    has_transparent_dropzones: boolean;
    drop_zone_shape: 'rectangle' | 'circle';
    draggable_container_position: 'top' | 'bottom' | 'left' | 'right';
    vocab_link_option: string;
  };
  lessonId?: number;
  locale?: string;
  onReadyToSwitchLocale?: (locale: string) => void;
  onSaveResult?: (succeeded: boolean) => void;
  questionId: number;
  questionTranslationId: number;
  requestedLocale?: string;
  saveControlRef?: React.MutableRefObject<SaveControl | null>;
  spanishImagesInMicAndDd?: boolean;
  userType: UserType;
}

const Builder = ({
  backgroundImage = { id: null, url: null },
  lessonId,
  locale = 'en',
  onReadyToSwitchLocale,
  onSaveResult,
  questionId,
  questionTranslationId,
  requestedLocale = locale,
  saveControlRef,
  data,
  spanishImagesInMicAndDd = false,
  userType,
}: BuilderProps) => {
  const modalManager = useModalManager();
  const [dropZones, dropZoneDispatch] = useReducer(dropZoneReducer, data.drop_zones);
  const [draggableChoices, draggableChoiceDispatch] = useReducer(draggableChoiceReducer, data.draggable_choices);
  const [correctAnswers, correctAnswerDispatch] = useReducer(correctAnswerReducer, data.correct_answers);
  const [dragAndDropSettings, dragAndDropSettingsDispatch] = useReducer(
    dragAndDropSettingsReducer,
    {
      draggableContainerPosition: data.draggable_container_position,
      dropZoneShape: data.drop_zone_shape,
      hasReusableAnswerChoices: data.has_reusable_answer_choices,
      hasTransparentAnswerChoices: data.has_transparent_answer_choices,
      hasTransparentDropzones: data.has_transparent_dropzones,
      vocabLinkOption: data.vocab_link_option,
    },
  );
  const {
    hasReusableAnswerChoices,
    hasTransparentAnswerChoices,
    hasTransparentDropzones,
  } = dragAndDropSettings as DragAndDropSettings;
  const [background, backgroundDispatch] = useReducer(backgroundReducer, backgroundImage);
  const [textAreas, textAreaDispatch] = useReducer<React.Reducer<any, any>>(textAreaReducer, data.text_areas);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [backgroundRef, setBackgroundRef] = useState();
  const [isEditingLabels, setIsEditingLabels] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [loading, setLoading] = useState(true);
  const setVocabularyTerms = useVocabularyStore(state => state.setVocabularyTerms);
  const [dropZoneShape, setDropZoneShape] = useState(data.drop_zone_shape || 'rectangle');
  const [draggableContainerPosition, setDraggableContainerPosition] = useState(data.draggable_container_position || 'left');
  const [vocabLinkOption, setVocabLinkOption] = useState(data.vocab_link_option || 'definition');
  const [shouldHideGrid, setShouldHideGrid] = useState(false);
  const [pendingVocabSpacing, setPendingVocabSpacing] = useState<{ linkOption: string; shape: string } | null>(null);

  const hasUnsavedChanges = useRef(false);
  const isFirstRender = useRef(true);
  const isSaveInFlight = useRef(false);
  const latestSave = useRef<Promise<void>>(Promise.resolve());

  const saveObjects = () => {
    hasUnsavedChanges.current = false;
    isSaveInFlight.current = true;
    setIsSaving(true);
    const route = Routes.plato_api_question_path(questionId);
    latestSave.current = Axios.put(route, {
      data: {
        correct_answers: correctAnswers,
        draggable_choices: draggableChoices,
        draggable_container_position: draggableContainerPosition,
        drop_zone_shape: dropZoneShape,
        drop_zones: dropZones,
        has_reusable_answer_choices: hasReusableAnswerChoices,
        has_transparent_answer_choices: hasTransparentAnswerChoices,
        has_transparent_dropzones: hasTransparentDropzones,
        text_areas: textAreas,
        vocab_link_option: vocabLinkOption,
      },
      locale,
    }).then(
      () => {
        isSaveInFlight.current = false;
        setIsSaving(false);
        setSaveFailed(false);
        onSaveResult?.(true);
      },
      () => {
        hasUnsavedChanges.current = true;
        isSaveInFlight.current = false;
        setIsSaving(false);
        setSaveFailed(true);
        onSaveResult?.(false);
      },
    );
    return latestSave.current;
  };

  const autosaveDeps = [
    correctAnswers,
    draggableChoices,
    dropZones,
    textAreas,
    hasReusableAnswerChoices,
    hasTransparentAnswerChoices,
    hasTransparentDropzones,
    dropZoneShape,
    draggableContainerPosition,
  ];

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    hasUnsavedChanges.current = true;
  }, autosaveDeps);

  useDebouncedEffect(() => {
    if (hasUnsavedChanges.current) saveObjects();
  }, autosaveDeps, 500);

  const flushPendingSave = () => (hasUnsavedChanges.current ? saveObjects() : latestSave.current);

  useImperativeHandle(saveControlRef, () => ({
    flushPendingSave,
    hasPendingSave: () => hasUnsavedChanges.current || isSaveInFlight.current,
  }));

  // AIDEV-NOTE: Flush any edit still waiting on the autosave debounce so it saves to the language it was made in.
  useEffect(() => {
    if (requestedLocale === locale) return;

    flushPendingSave().then(() => onReadyToSwitchLocale?.(requestedLocale));
  }, [requestedLocale]);

  useEffect(() => {
    if (!lessonId) return;
    const route = Routes.plato_api_get_glossary_terms_glossary_terms_path({ lesson_id: lessonId });

    Axios.get(route)
      .then((response) => {
        if (response.data.glossary_terms) setVocabularyTerms(formatVocab(response.data.glossary_terms));
        setLoading(false);
      }).catch((error) => {
      console.error(error);
    });
  }, []);

  // After linking vocab, the new text areas need to be laid out from their rendered heights. The
  // text areas are created via a dispatch inside the dropzone reducer, so they land across more than
  // one commit — we defer the measurement to the next frame (re-scheduling as those commits arrive)
  // so every box exists and has laid out its content before we measure it. Pure helpers do the rest.
  useLayoutEffect(() => {
    if (!pendingVocabSpacing) return undefined;

    const frame = requestAnimationFrame(() => {
      const heightsPct = measureHeightsPct(Object.keys(textAreas));
      const spaced = computeSpacedTextAreas(textAreas, heightsPct, pendingVocabSpacing);
      const matched = matchDropzonesToTextAreas(dropZones, spaced, pendingVocabSpacing.linkOption);

      textAreaDispatch({ payload: spaced, type: 'SET_SPACED' });
      dropZoneDispatch({ payload: matched, type: 'SET_MATCHED' });
      setPendingVocabSpacing(null);
    });

    return () => cancelAnimationFrame(frame);
  }, [pendingVocabSpacing, textAreas, dropZones]);

  return (
    <BuilderContext.Provider
      value={{
        backgroundDispatch,
        backgroundRef,
        correctAnswerDispatch,
        dragAndDropSettingsDispatch,
        draggableChoiceDispatch,
        draggableChoices,
        draggableContainerPosition,
        dropZoneDispatch,
        dropZoneShape,
        dropZones,
        hasTransparentAnswerChoices,
        hasTransparentDropzones,
        isCopying,
        isEditingLabels,
        loading,
        modalManager,
        questionId,
        setBackgroundRef,
        setDraggableContainerPosition,
        setDropZoneShape,
        setIsCopying,
        setIsEditingLabels,
        setPendingVocabSpacing,
        setShouldHideGrid,
        setVocabLinkOption,
        shouldHideGrid,
        textAreaDispatch,
        textAreas,
        vocabLinkOption,
      }}
    >
      <DndProvider backend={MultiBackend} options={HTML5toTouch}>
        <SectionHeader
          header={I18n.t('drag_drop_dropzones_header')}
          subHeader={I18n.t('drag_drop_dropzones_subheader')}
        />
        <div className={styles.controlsHeader}>
          <DropZoneButton />
          <BackgroundImageButton
            background={background}
            questionId={questionId}
            spanishImagesInMicAndDd={spanishImagesInMicAndDd}
            userType={userType}
          />
          <AddTextAreaButton />
          <div>
            <SettingsButton
              hasReusableAnswerChoices={hasReusableAnswerChoices}
              hasTransparentAnswerChoices={hasTransparentAnswerChoices}
              hasTransparentDropzones={hasTransparentDropzones}
            />
          </div>
          <CopyingText />
        </div>
        <div className="tw-flex tw-justify-center">
          <Container
            {...{
              background,
              correctAnswers,
              draggableChoices,
              dropZones,
              isSaving,
              questionTranslationId,
              saveFailed,
              shouldHideGrid,
              textAreas,
            }}
            snapToGrid={SNAP_TO_GRID}
          />
        </div>
        <CustomDragLayer snapToGrid={SNAP_TO_GRID} />
        <SectionHeader
          header={I18n.t('drag_drop_draggable_header')}
          subHeader={I18n.t('drag_drop_draggable_subheader')}
        />
        {lessonId && (
          <LinkVocabularyWrapper
            text="Link Vocabulary"
            icon="fa-link"
          />
        )}
        <DraggableChoiceEditor draggableChoices={draggableChoices} isAdmin={userType === 'Sysadmin'} />
      </DndProvider>
    </BuilderContext.Provider>
  );
};

export default Builder;
